import * as R from 'ramda';
import { findProductColorPacksToOffline } from '../../../../../../../../../services/modules/product-colors-packs/product-colors-packs.repository';
import { ProductFromApiToOffline } from '../../../../../../../../../services/modules/productsSearch/productsSearch.types';
import { AvailabilityEnum } from '../../../../../../../../products/ProductTypes';
import { OfflineDatabaseDeliveryWindow } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseDeliveryWindow.types';
import { OfflineDatabaseFutureStock } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseFutureStock.types';
import { OfflineDatabasePack } from '../../../../../../../database/offlineDatabase/types/offlineDatabasePack.types';
import { OfflineDatabaseProduct } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseProduct.types';
import { OfflineDatabaseProductColor } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseProductColor.types';
import { OfflineDatabaseProductColorPriceInfo } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseProductColorPriceInfo.types';
import { OfflineDatabaseSize } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseSize.types';
import { OfflineDatabaseSku } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseSku.types';
import { OfflineDatabaseStock } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseStock.types';
import { offlineDeliveryWindowsRepository } from '../../../../../../../repositories/offlineDeliveryWindows.repository';
import { offlineFutureStocksRepository } from '../../../../../../../repositories/offlineFutureStocks.repository';
import { offlinePacksRepository } from '../../../../../../../repositories/offlinePacks.repository';
import { offlineProductColorPriceInfoRepository } from '../../../../../../../repositories/offlineProductColorPriceInfo.repository';
import { offlineProductColorsRepository } from '../../../../../../../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../../../../../../../repositories/offlineProducts.repository';
import { offlineSizesRepository } from '../../../../../../../repositories/offlineSizes.repository';
import { offlineSkusRepository } from '../../../../../../../repositories/offlineSkus.repository';
import { offlineStocksRepository } from '../../../../../../../repositories/offlineStocks.repository';
import { QueryKeysAndFnOptions } from '../../../useQueryKeysAndFnToCache.types';
import { generateQueryKeysAndFnToPaginatedEndpoint } from '../../generateQueryKeysAndFnToPaginatedEndpoint';
import { QueryKeysAndFnGenerator, QueryKeysAndFnGeneratorParams } from './types';
import { ImageToCache } from './types/offlineImageCacher.util.types';
import { GetProductWithCachedMediasParams } from './types/productsQueryKeysAndFnGenerator.types';
import { OfflineImageCacher } from './utils/OfflineImageCacher.util';
import { SaveEntityWithDebounceHandler } from './utils/SaveEntityWithDebounceHandler.util';

export class ProductsQueryKeysAndFnGenerator implements QueryKeysAndFnGenerator {
  // todo: decidir quantas imagens baixar e de que tamanho
  private readonly IMAGES_CHUNK_SIZE = 20;
  private readonly MAX_IMAGE_CACHE_SIZE_1_GB_IN_BYTES = 1 * 1000 * 1024 * 1024;
  private readonly NUMBER_OF_MEDIAS_TO_CACHE_BY_PRODUCT_COLOR = 3;

  private readonly offlineImageCacher: OfflineImageCacher;
  private saveSizesWithDebounceHandler: SaveEntityWithDebounceHandler<OfflineDatabaseSize>;
  private savePacksWithDebounceHandler: SaveEntityWithDebounceHandler<OfflineDatabasePack>;
  private saveProductsWithDebounceHandler: SaveEntityWithDebounceHandler<OfflineDatabaseProduct>;
  private saveDeliveryWindowsWithDebounceHandler: SaveEntityWithDebounceHandler<OfflineDatabaseDeliveryWindow>;

  constructor(
    private readonly params: Pick<QueryKeysAndFnGeneratorParams, 'priority' | 'webpEnabled'>,
  ) {
    const DEBOUNCE_TIME = 5000;

    this.saveSizesWithDebounceHandler = new SaveEntityWithDebounceHandler(
      DEBOUNCE_TIME,
      this.saveSize,
    );

    this.saveProductsWithDebounceHandler = new SaveEntityWithDebounceHandler(
      DEBOUNCE_TIME,
      this.saveProduct,
    );

    this.saveDeliveryWindowsWithDebounceHandler = new SaveEntityWithDebounceHandler(
      DEBOUNCE_TIME,
      this.saveDeliveryWindow,
    );

    this.savePacksWithDebounceHandler = new SaveEntityWithDebounceHandler(
      DEBOUNCE_TIME,
      this.savePack,
    );
    this.offlineImageCacher = new OfflineImageCacher(
      this.MAX_IMAGE_CACHE_SIZE_1_GB_IN_BYTES,
      this.IMAGES_CHUNK_SIZE,
      this.params.webpEnabled,
    );
  }

  async generateQueryKeysAndFn(): Promise<QueryKeysAndFnOptions> {
    const queryKeysAndFnToOfflineProducts =
      await generateQueryKeysAndFnToPaginatedEndpoint<ProductFromApiToOffline>({
        url: '/catalog-product-colors/offline/v1',
        priority: this.params.priority,
        fnToSaveData: async (data) => {
          await this.handleSaveAll(data);
        },
      });

    const firstItem = queryKeysAndFnToOfflineProducts.shift();

    if (!firstItem) {
      return [];
    }

    const firstItemWithValidateIfCanFinishAllProcess = {
      ...firstItem,
      validateIfCanFinishAllProcess: () => {
        const allDebouncesLoaded =
          this.savePacksWithDebounceHandler.hasSavedAll() &&
          this.saveSizesWithDebounceHandler.hasSavedAll() &&
          this.saveProductsWithDebounceHandler.hasSavedAll() &&
          this.saveDeliveryWindowsWithDebounceHandler.hasSavedAll();

        return allDebouncesLoaded;
      },
    };

    return [firstItemWithValidateIfCanFinishAllProcess, ...queryKeysAndFnToOfflineProducts];
  }

  private async saveSize(size: OfflineDatabaseSize) {
    const alreadyAdded = await offlineSizesRepository.getById(size.id);
    if (!alreadyAdded) {
      await offlineSizesRepository.save(size);
    }
  }

  private async saveProduct(product: OfflineDatabaseProduct) {
    const alreadyAdded = await offlineProductsRepository.getById(product.id);
    if (!alreadyAdded) {
      await offlineProductsRepository.save(product);
    }
  }

  private async saveDeliveryWindow(dw: OfflineDatabaseDeliveryWindow) {
    const alreadyAdded = await offlineDeliveryWindowsRepository.getById(dw.id);
    if (!alreadyAdded) {
      await offlineDeliveryWindowsRepository.save(dw);
    }
  }

  private async savePack(pack: OfflineDatabasePack) {
    const alreadyAdded = await offlinePacksRepository.getById(pack.id);
    if (!alreadyAdded) {
      await offlinePacksRepository.save(pack);
    }
  }

  private async getProductsWithCachedMedias(
    data: ProductFromApiToOffline[],
  ): Promise<ProductFromApiToOffline[]> {
    const { productMediaUrls, productColorImageUrls } = this.getImageUrlsToCache(data);
    const { successItemsMap: cachedProductMedias } = await this.offlineImageCacher.cacheImages(
      productMediaUrls,
      'lg',
    );
    const { successItemsMap: cachedProductColorImages } = await this.offlineImageCacher.cacheImages(
      productColorImageUrls,
      'xs',
    );
    return this.getProductWithCachedMedias({
      data,
      cachedProductMedias,
      cachedProductColorImages,
    });
  }

  private getImageUrlsToCache(data: ProductFromApiToOffline[]) {
    const productMediaUrls: ImageToCache[] = [];
    const productColorImageUrls: ImageToCache[] = [];

    data.forEach((product) => {
      this.getLimitedProductMedias(product).forEach((media) => {
        productMediaUrls.push({
          id: media.id,
          url: media.url,
        });
      });

      const colorImageUrl = product.productColor.imageUrl;
      colorImageUrl &&
        productColorImageUrls.push({
          id: product.productColor.id,
          url: colorImageUrl,
        });
    });

    return { productMediaUrls, productColorImageUrls };
  }

  private getProductWithCachedMedias({
    data,
    cachedProductMedias,
    cachedProductColorImages,
  }: GetProductWithCachedMediasParams): ProductFromApiToOffline[] {
    const productWithCachedMedias = data.map((product) => {
      const limited = this.getLimitedProductMedias(product);

      const cachedMedias = limited.map((media) => {
        const cached = cachedProductMedias.get(media.id);
        return {
          ...media,
          url: cached ?? media.url,
        };
      });

      const cachedImageUrl = cachedProductColorImages.get(product.productColor.id);

      return {
        ...product,
        productColor: {
          ...product.productColor,
          imageUrl: cachedImageUrl ?? product.productColor.imageUrl,
          medias: cachedMedias,
        },
      };
    });

    return productWithCachedMedias;
  }

  private getLimitedProductMedias(
    product: ProductFromApiToOffline,
  ): ProductFromApiToOffline['productColor']['medias'] {
    return product.productColor.medias.slice(0, this.NUMBER_OF_MEDIAS_TO_CACHE_BY_PRODUCT_COLOR);
  }

  private async addPacksInProductColorsAndReturnOfflinePacks(
    productColors: OfflineDatabaseProductColor[],
  ): Promise<{
    offlinePacks: OfflineDatabasePack[];
    productColorsWithPacks: OfflineDatabaseProductColor[];
  }> {
    const offlinePacks: OfflineDatabasePack[] = [];

    const productColorIds = productColors.map((productColor) => productColor.id);
    const packsToOfflineResponse = await findProductColorPacksToOffline(productColorIds);

    if (!packsToOfflineResponse.ok) {
      console.error('[addPacksInProductColorsAndReturnOfflinePacks] error to fetch packs', {
        productColorIds,
        packsToOfflineResponse,
      });

      throw packsToOfflineResponse.originalError;
    }

    const packsToOfflineData = packsToOfflineResponse.data;

    packsToOfflineData?.packs.forEach((pack) => {
      offlinePacks.push({
        id: pack.id,
        name: pack.name,
        quantitiesBySize: pack.quantitiesBySize,
      });
    });

    const productColorsWithPacks = productColors.map((productColor) => {
      const immediatePacks = packsToOfflineData?.immediateProductColorPacks.filter(
        (pack) => pack.productColorId === productColor.id,
      );
      const preOrderPacks = packsToOfflineData?.preOrderProductColorPacks.filter(
        (pack) => pack.productColorId === productColor.id,
      );

      return {
        ...productColor,
        preOrderPacks,
        immediatePacks,
      };
    });

    return {
      offlinePacks,
      productColorsWithPacks,
    };
  }

  private async transformInOfflineEntities(data: ProductFromApiToOffline[]) {
    const offlineSkus: OfflineDatabaseSku[] = [];
    const offlineSizes: OfflineDatabaseSize[] = [];
    const offlineProducts: OfflineDatabaseProduct[] = [];
    const offlineStocks: OfflineDatabaseStock[] = [];
    const offlineFutureStocks: OfflineDatabaseFutureStock[] = [];
    const offlineProductColors: OfflineDatabaseProductColor[] = [];
    const offlineDeliveryWindows: OfflineDatabaseDeliveryWindow[] = [];
    const offlineProductColorPriceInfos: OfflineDatabaseProductColorPriceInfo[] = [];

    const productsWithMediaUrl = await this.getProductsWithCachedMedias(data);

    productsWithMediaUrl.forEach((product) => {
      const preOrderDeliveryWindows = product.deliveryWindows.preOrder || [];
      const futureStockDeliveryWindows = product.deliveryWindows.futureStock || [];

      const deliveryWindows = [...preOrderDeliveryWindows, ...futureStockDeliveryWindows];
      const uniqueDeliveryWindows = R.uniqBy(R.prop('id'), deliveryWindows);

      uniqueDeliveryWindows.forEach((dw) => {
        offlineDeliveryWindows.push({
          id: dw.id,
          name: dw.name,
          code: dw.code,
          endDate: dw.endDate,
          startDate: dw.startDate,
        });
      });

      offlineProducts.push({
        id: product.productId,
        code: product.productCode,
        name: product.productName,
        retailPrice: product.productRetailPrice,
        wholesalePrice: product.productWholesalePrice,
        categoryId: product.categoryId,
        collectionId: product.collectionId,
        sizeIds: product.sizes.map((s) => s.id),
        referenceCode: product.productReferenceCode ?? undefined,
        description: product.productDescription ?? undefined,
        resume: product.productResume ?? undefined,
        rules: product.productRules,
      });

      offlineProductColors.push({
        id: product.productColor.id,
        productId: product.productId,
        colorId: product.productColor.colorId,
        code: product.productColor.code,
        name: product.productColor.name,
        isImage: product.productColor.isImage,
        hexcode: product.productColor.hexcode,
        imageUrl: product.productColor.imageUrl,
        medias: product.productColor.medias,
        referenceColorIds: product.productColor.referenceColorIds,
        classificationIds: product.productColor.classificationIds,
        allowChangePreOrderGrid: product.productColor.allowChangePreOrderGrid,
        allowChangeImmediateGrid: product.productColor.allowChangeImmediateGrid,
        availabilities: product.productColor.availabilities as AvailabilityEnum[],
        allowPackPartialApplyInImmediate: product.productColor.allowPackPartialApplyInImmediate,
        allowPackPartialApplyInPreOrder: product.productColor.allowPackPartialApplyInPreOrder,
        deliveryWindowIds: {
          preOrder: preOrderDeliveryWindows.map((dw) => dw.id),
          futureStock: futureStockDeliveryWindows.map((dw) => dw.id),
        },
      });

      product.productColor.prices.forEach((price) => {
        offlineProductColorPriceInfos.push({
          version: 2,
          priceTableId: price.priceTableId,
          pricesToSizes: price.pricesToSizes,
          productColorId: product.productColor.id,
        });
      });

      product.sizes.forEach((size) => {
        offlineSizes.push({
          id: size.id,
          name: size.name,
          unique: size.unique,
          position: size.position,
        });

        if (size.sku?.id && size.sku.code) {
          offlineSkus.push({
            id: size.sku.id,
            sizeId: size.id,
            code: size.sku.code,
            productId: product.productId,
            productColorId: product.productColor.id,
            preOrderEndDate: size.sku.preOrderEndDate,
            productSizeActive: size.productSizeActive,
            preOrderStartDate: size.sku.preOrderStartDate,
            preOrderMinimumQuantity: size.sku.preOrderMinimumQuantity,
            preOrderDeliveryWindowIds: size.preOrderDeliveryWindowIds,
            availableForPreOrder: size.sku.availableForPreOrder as boolean,
          });
        }

        if (size.futureStocks && size.sku?.id) {
          size.futureStocks.forEach((fs) => {
            offlineFutureStocks.push({
              id: fs.id,
              skuId: size.sku!.id!,
              productId: product.productId,
              productColorId: product.productColor.id,
              deliveryWindowId: fs.deliveryWindowId,
              endDate: fs.endDate,
              startDate: fs.startDate,
              totalAmount: fs.totalAmount,
              availableAmount: fs.availableAmount,
            });
          });
        }

        if (size.stock && size.sku?.id) {
          offlineStocks.push({
            id: size.stock.id,
            skuId: size.sku.id,
            productId: product.productId,
            productColorId: product.productColor.id,
            endDate: size.stock.endDate,
            startDate: size.stock.startDate,
            totalAmount: size.stock.totalAmount,
            availableAmount: size.stock.availableAmount,
          });
        }
      });
    });

    const { offlinePacks, productColorsWithPacks } =
      await this.addPacksInProductColorsAndReturnOfflinePacks(offlineProductColors);

    return {
      offlineSkus,
      offlineSizes,
      offlinePacks,
      offlineStocks,
      offlineProducts,
      offlineFutureStocks,
      offlineDeliveryWindows,
      offlineProductColorPriceInfos,
      offlineProductColors: productColorsWithPacks,
    };
  }

  private async handleSaveAll(data: ProductFromApiToOffline[]) {
    const {
      offlineSkus,
      offlinePacks,
      offlineSizes,
      offlineStocks,
      offlineProducts,
      offlineFutureStocks,
      offlineProductColors,
      offlineDeliveryWindows,
      offlineProductColorPriceInfos,
    } = await this.transformInOfflineEntities(data);

    await offlineSkusRepository.saveAll(offlineSkus);
    await offlineStocksRepository.saveAll(offlineStocks);
    await offlineFutureStocksRepository.saveAll(offlineFutureStocks);
    await offlineProductColorsRepository.saveAll(offlineProductColors);
    await offlineProductColorPriceInfoRepository.saveAll(offlineProductColorPriceInfos);

    offlinePacks.forEach((pack) => this.savePacksWithDebounceHandler.save(pack));
    offlineSizes.forEach((size) => this.saveSizesWithDebounceHandler.save(size));
    offlineProducts.forEach((product) => this.saveProductsWithDebounceHandler.save(product));
    offlineDeliveryWindows.forEach((dw) => this.saveDeliveryWindowsWithDebounceHandler.save(dw));
  }
}
